class CustomVariantDropdown extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') {
      return;
    }

    this.dataset.initialized = 'true';

    this.closeTimer = null;

    this.trigger =
      this.querySelector(
        '[data-dropdown-trigger]'
      );

    this.menu =
      this.querySelector(
        '[data-dropdown-menu]'
      );

    this.value =
      this.querySelector(
        '[data-dropdown-value]'
      );

    this.options =
      Array.from(
        this.querySelectorAll(
          '[data-dropdown-option]'
        )
      );

    this.nativeSelect =
      this.querySelector(
        '[data-variant-selector]'
      );


    if (
      !this.trigger ||
      !this.menu
    ) {
      return;
    }


    /* -----------------------------------------
       Bind
       ----------------------------------------- */

    this.handleTriggerClick =
      this.handleTriggerClick.bind(this);

    this.handleOptionClick =
      this.handleOptionClick.bind(this);

    this.handleDocumentClick =
      this.handleDocumentClick.bind(this);

    this.handleKeydown =
      this.handleKeydown.bind(this);

    this.repositionDropdown =
      this.repositionDropdown.bind(this);


    /* -----------------------------------------
       Trigger
       ----------------------------------------- */

    this.trigger.addEventListener(
      'click',
      this.handleTriggerClick
    );

    this.trigger.addEventListener(
      'keydown',
      this.handleKeydown
    );


    /* -----------------------------------------
       Options
       ----------------------------------------- */

    this.options.forEach(option => {

      option.addEventListener(
        'click',
        this.handleOptionClick
      );

      option.addEventListener(
        'keydown',
        this.handleKeydown
      );

    });


    /* -----------------------------------------
       Outside click
       ----------------------------------------- */

    document.addEventListener(
      'click',
      this.handleDocumentClick
    );
  }


  disconnectedCallback() {
    this.trigger?.removeEventListener(
      'click',
      this.handleTriggerClick
    );

    this.trigger?.removeEventListener(
      'keydown',
      this.handleKeydown
    );


    this.options?.forEach(option => {

      option.removeEventListener(
        'click',
        this.handleOptionClick
      );

      option.removeEventListener(
        'keydown',
        this.handleKeydown
      );

    });


    document.removeEventListener(
      'click',
      this.handleDocumentClick
    );


    window.removeEventListener(
      'scroll',
      this.repositionDropdown,
      true
    );

    window.removeEventListener(
      'resize',
      this.repositionDropdown
    );


    if (this.closeTimer) {
      window.clearTimeout(
        this.closeTimer
      );

      this.closeTimer = null;
    }


    /*
     * Important for dynamically replaced
     * Quick Look content.
     *
     * The menu may currently live under <body>.
     * Return it before this custom element dies.
     */
    this.finishClose();
  }


  /* =====================================================
     TRIGGER
     ===================================================== */

  handleTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();


    const isOpen =
      this.trigger.getAttribute(
        'aria-expanded'
      ) === 'true';


    if (isOpen) {

      this.closeDropdown();

    } else {

      this.closeOtherDropdowns();

      this.openDropdown();

    }
  }


  /* =====================================================
     CLOSE OTHER DROPDOWNS
     ===================================================== */

  closeOtherDropdowns() {
    document
      .querySelectorAll(
        'custom-variant-dropdown'
      )
      .forEach(component => {

        if (
          component !== this &&
          typeof component.closeDropdown ===
            'function'
        ) {

          component.closeDropdown();

        }

      });
  }


  /* =====================================================
     OPEN
     ===================================================== */

  openDropdown() {
    if (
      !this.menu ||
      !this.trigger ||
      this.trigger.getAttribute(
        'aria-expanded'
      ) === 'true'
    ) {
      return;
    }


    if (this.closeTimer) {

      window.clearTimeout(
        this.closeTimer
      );

      this.closeTimer = null;


      this.menu.classList.remove(
        'is-animating-out'
      );
    }


    /*
     * Remember where the menu belongs.
     */
    this.menuOriginalParent =
      this.menu.parentNode;

    this.menuOriginalNextSibling =
      this.menu.nextSibling;


    /*
    * Product-card dropdowns can portal to body.
    *
    * Dropdowns inside a native modal <dialog>
    * must remain inside that dialog's top layer,
    * otherwise no z-index can place them above it.
    */
    const containingDialog =
    this.closest('dialog[open]');

    this.menuPortalTarget =
    containingDialog ||
    document.body;

    this.menuPortalTarget.appendChild(
    this.menu
    );


    this.menu.hidden =
      false;


    this.trigger.setAttribute(
      'aria-expanded',
      'true'
    );


    this.repositionDropdown();


    /*
     * Opening animation.
     */
    this.menu.classList.add(
      'is-animating-in'
    );


    requestAnimationFrame(() => {

      requestAnimationFrame(() => {

        this.menu?.classList.remove(
          'is-animating-in'
        );

      });

    });


    this.centerSelectedOption();


    window.addEventListener(
      'scroll',
      this.repositionDropdown,
      true
    );


    window.addEventListener(
      'resize',
      this.repositionDropdown
    );
  }


  /* =====================================================
     CLOSE
     ===================================================== */

  closeDropdown({
    immediate = false
  } = {}) {

    if (
      !this.menu ||
      !this.trigger
    ) {
      return;
    }


    const isOpen =
      this.trigger.getAttribute(
        'aria-expanded'
      ) === 'true';


    if (!isOpen && !immediate) {
      return;
    }


    this.trigger.setAttribute(
      'aria-expanded',
      'false'
    );


    window.removeEventListener(
      'scroll',
      this.repositionDropdown,
      true
    );


    window.removeEventListener(
      'resize',
      this.repositionDropdown
    );


    this.menu.classList.remove(
      'is-animating-in'
    );


    if (immediate) {

      this.finishClose();

      return;
    }


    this.menu.classList.add(
      'is-animating-out'
    );


    if (this.closeTimer) {

      window.clearTimeout(
        this.closeTimer
      );

    }


    this.closeTimer =
      window.setTimeout(
        () => {

          this.finishClose();

          this.closeTimer =
            null;

        },
        150
      );
  }


  finishClose() {
    if (!this.menu) {
      return;
    }


    this.menu.hidden =
      true;


    this.menu.classList.remove(
      'is-animating-in',
      'is-animating-out',
      'opens-upward'
    );


    /*
     * Return menu to the original
     * custom-element DOM.
     */
    if (this.menuOriginalParent) {

      if (
        this.menuOriginalNextSibling &&
        this.menuOriginalNextSibling.parentNode ===
          this.menuOriginalParent
      ) {

        this.menuOriginalParent.insertBefore(
          this.menu,
          this.menuOriginalNextSibling
        );

      } else {

        this.menuOriginalParent.appendChild(
          this.menu
        );

      }

    }


    /*
     * Remove temporary popover positioning.
     */
    this.menu.style.position = '';

    this.menu.style.left = '';

    this.menu.style.top = '';

    this.menu.style.width = '';

    this.menu.style.maxHeight = '';

    this.menu.style.zIndex = '';

    this.menuOriginalParent = null;

    this.menuOriginalNextSibling = null;

    this.menuPortalTarget = null;
  }


  /* =====================================================
     OUTSIDE CLICK
     ===================================================== */

  handleDocumentClick(event) {
    const clickedTrigger =
      this.trigger?.contains(
        event.target
      );


    /*
     * Menu may currently be under <body>,
     * so don't rely on this.contains().
     */
    const clickedMenu =
      this.menu?.contains(
        event.target
      );


    if (
      !clickedTrigger &&
      !clickedMenu
    ) {

      this.closeDropdown();

    }
  }


  /* =====================================================
     POSITION
     ===================================================== */

  repositionDropdown() {
    if (
      !this.menu ||
      this.menu.hidden ||
      !this.trigger
    ) {
      return;
    }


    const rect =
      this.trigger.getBoundingClientRect();


    const viewportHeight =
      window.innerHeight;


    const viewportWidth =
      window.innerWidth;


    const gap =
      5;


    const edgePadding =
      10;


    const spaceBelow =
      viewportHeight -
      rect.bottom -
      edgePadding;


    const spaceAbove =
      rect.top -
      edgePadding;


    const preferredMaxHeight =
      viewportWidth <= 749
        ? 320
        : 260;


    const shouldOpenUpward =
      spaceBelow < 180 &&
      spaceAbove > spaceBelow;


    this.menu.classList.toggle(
      'opens-upward',
      shouldOpenUpward
    );


    const availableSpace =
      shouldOpenUpward
        ? spaceAbove
        : spaceBelow;


    const maxHeight =
      Math.max(
        120,
        Math.min(
          preferredMaxHeight,
          availableSpace
        )
      );


    this.menu.style.position =
      'fixed';


    this.menu.style.width =
      `${rect.width}px`;


    this.menu.style.maxHeight =
      `${maxHeight}px`;


    this.menu.style.zIndex =
      '999999';


    const left =
      Math.max(
        edgePadding,
        Math.min(
          rect.left,
          viewportWidth -
            rect.width -
            edgePadding
        )
      );


    this.menu.style.left =
      `${left}px`;


    const menuHeight =
      this.menu.offsetHeight;


    if (shouldOpenUpward) {

      this.menu.style.top =
        `${
          Math.max(
            edgePadding,
            rect.top -
              menuHeight -
              gap
          )
        }px`;

    } else {

      this.menu.style.top =
        `${
          Math.min(
            rect.bottom + gap,
            viewportHeight -
              menuHeight -
              edgePadding
          )
        }px`;

    }
  }


  /* =====================================================
     CENTER CURRENT OPTION
     ===================================================== */

  centerSelectedOption() {
    if (!this.menu) {
      return;
    }


    const selected =
      this.menu.querySelector(
        '[data-dropdown-option][aria-selected="true"]'
      );


    if (!selected) {
      return;
    }


    const menuHeight =
      this.menu.clientHeight;


    const optionTop =
      selected.offsetTop;


    const optionHeight =
      selected.offsetHeight;


    this.menu.scrollTop =
      optionTop -
      menuHeight / 2 +
      optionHeight / 2;
  }


  /* =====================================================
     OPTION CLICK
     ===================================================== */

  handleOptionClick(event) {
    const option =
        event.currentTarget;

    if (
        !option ||
        option.disabled
    ) {
        return;
    }

    const variantId =
        option.dataset.variantId;

    if (!variantId) {
        return;
    }


    /* =====================================================
        1. UPDATE DROPDOWN VISUAL STATE
        ===================================================== */

    this.updateSelectedValue(
        option
    );

    this.updateOptionStates(
        variantId
    );


    /* =====================================================
        2. UPDATE NATIVE SELECT VALUE
        ===================================================== */

    if (this.nativeSelect) {
        this.nativeSelect.value =
        String(variantId);
    }


    /* =====================================================
        3. EMIT COMPONENT EVENT
        
        Product-card controller uses this because the
        visual menu may currently be portalled elsewhere.
        ===================================================== */

    this.dispatchEvent(
        new CustomEvent(
        'variant-dropdown:change',
        {
            bubbles: true,

            detail: {
            variantId:
                String(variantId),

            option
            }
        }
        )
    );


    /* =====================================================
        4. EMIT NATIVE SELECT CHANGE
        
        Quick Look uses this:
        
        native select
            ↓
        handleContentChange()
            ↓
        changeVariant()
        
        Do this AFTER updating select.value.
        ===================================================== */

    if (this.nativeSelect) {
        this.nativeSelect.dispatchEvent(
        new Event(
            'change',
            {
            bubbles: true
            }
        )
        );
    }


    /* =====================================================
        5. CLOSE DROPDOWN
        ===================================================== */

    this.closeDropdown();


    /* =====================================================
        6. RESTORE FOCUS
        ===================================================== */

    this.trigger?.focus({
        preventScroll: true
    });
    }


  /* =====================================================
     VALUE LABEL
     ===================================================== */

  updateSelectedValue(option) {
    if (!this.value) {
      return;
    }


    const title =
      option.querySelector(
        '.custom-variant-dropdown__option-title'
      );


    if (!title) {
      return;
    }


    const soldOut =
      title.querySelector(
        '.custom-variant-dropdown__soldout'
      );


    let value =
      title.textContent.trim();


    if (soldOut) {

      value =
        value
          .replace(
            soldOut.textContent.trim(),
            ''
          )
          .trim();

    }


    this.value.textContent =
      value;
  }


  /* =====================================================
     SELECTED OPTION STATE
     ===================================================== */

  updateOptionStates(variantId) {
    this.options.forEach(item => {

      const selected =
        item.dataset.variantId ===
        String(variantId);


      item.setAttribute(
        'aria-selected',
        selected
          ? 'true'
          : 'false'
      );


      const check =
        item.querySelector(
          '[data-option-check]'
        );


      if (check) {

        check.hidden =
          !selected;

      }

    });
  }


  /* =====================================================
     KEYBOARD
     ===================================================== */

  handleKeydown(event) {
    const availableOptions =
      this.options.filter(
        option =>
          !option.disabled
      );


    if (!availableOptions.length) {
      return;
    }


    const activeElement =
      document.activeElement;


    const activeIndex =
      availableOptions.indexOf(
        activeElement
      );


    switch (event.key) {

      /* -----------------------------------------
         ESC
         ----------------------------------------- */

      case 'Escape':

        if (
          this.trigger.getAttribute(
            'aria-expanded'
          ) === 'true'
        ) {

          event.preventDefault();

          event.stopPropagation();


          this.closeDropdown();


          this.trigger.focus({
            preventScroll: true
          });

        }

        break;


      /* -----------------------------------------
         DOWN
         ----------------------------------------- */

      case 'ArrowDown':

        event.preventDefault();


        if (
          activeElement ===
          this.trigger
        ) {

          this.openDropdown();


          const selected =
            availableOptions.find(
              option =>
                option.getAttribute(
                  'aria-selected'
                ) === 'true'
            );


          requestAnimationFrame(() => {

            (
              selected ||
              availableOptions[0]
            )?.focus({
              preventScroll: true
            });

          });


          return;
        }


        availableOptions[
          activeIndex <
          availableOptions.length - 1
            ? activeIndex + 1
            : 0
        ]?.focus({
          preventScroll: true
        });


        break;


      /* -----------------------------------------
         UP
         ----------------------------------------- */

      case 'ArrowUp':

        event.preventDefault();


        if (
          activeElement ===
          this.trigger
        ) {

          this.openDropdown();


          const selected =
            availableOptions.find(
              option =>
                option.getAttribute(
                  'aria-selected'
                ) === 'true'
            );


          requestAnimationFrame(() => {

            (
              selected ||
              availableOptions[
                availableOptions.length - 1
              ]
            )?.focus({
              preventScroll: true
            });

          });


          return;
        }


        availableOptions[
          activeIndex > 0
            ? activeIndex - 1
            : availableOptions.length - 1
        ]?.focus({
          preventScroll: true
        });


        break;


      /* -----------------------------------------
         HOME
         ----------------------------------------- */

      case 'Home':

        if (activeIndex !== -1) {

          event.preventDefault();


          availableOptions[0]
            ?.focus({
              preventScroll: true
            });

        }

        break;


      /* -----------------------------------------
         END
         ----------------------------------------- */

      case 'End':

        if (activeIndex !== -1) {

          event.preventDefault();


          availableOptions[
            availableOptions.length - 1
          ]?.focus({
            preventScroll: true
          });

        }

        break;


      /* -----------------------------------------
         SELECT
         ----------------------------------------- */

      case 'Enter':
      case ' ':

        if (activeIndex !== -1) {

          event.preventDefault();

          activeElement.click();

        }

        break;
    }
  }
}


if (
  !customElements.get(
    'custom-variant-dropdown'
  )
) {

  customElements.define(
    'custom-variant-dropdown',
    CustomVariantDropdown
  );

}